import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, fileToBase64, API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import ComplianceBanner from '@/components/ComplianceBanner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BookOpen, ArrowLeft, Upload, FileText, Trash2, History, Edit3, Download } from 'lucide-react';

export default function SOPDetailPage() {
  const { id } = useParams();
  const { user, isManager } = useAuth();
  const [sop, setSop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', purpose: '', responsible: '', steps: '', change_note: '' });
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sops/${id}`);
      setSop(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const toggleStep = async (stepId, done) => {
    const newSteps = sop.steps.map((s) => s.id === stepId ? { ...s, done } : s);
    setSop({ ...sop, steps: newSteps });
    await api.patch(`/sops/${id}`, { steps: newSteps });
  };

  const openEdit = () => {
    setEditForm({
      title: sop.title, purpose: sop.purpose, responsible: sop.responsible,
      steps: sop.steps.map((s) => s.text).join('\n'),
      change_note: '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const newSteps = editForm.steps.split('\n').map((s) => s.trim()).filter(Boolean).map((t, i) => {
      const existing = sop.steps[i];
      return { id: existing?.id || crypto.randomUUID(), text: t, done: existing?.done || false };
    });
    await api.patch(`/sops/${id}`, {
      title: editForm.title, purpose: editForm.purpose, responsible: editForm.responsible,
      steps: newSteps, change_note: editForm.change_note,
    });
    setEditOpen(false);
    load();
  };

  const upload = async (file) => {
    if (!file) return;
    const data_base64 = await fileToBase64(file);
    await api.post(`/sops/${id}/attachments`, {
      filename: file.name, mime_type: file.type || 'application/octet-stream', data_base64,
    });
    load();
  };

  const downloadAttachment = async (att) => {
    const res = await api.get(`/sops/${id}/attachments/${att.id}`);
    const a = document.createElement('a');
    a.href = `data:${res.data.mime_type};base64,${res.data.data_base64}`;
    a.download = res.data.filename;
    a.click();
  };

  const deleteAttachment = async (att) => {
    await api.delete(`/sops/${id}/attachments/${att.id}`);
    load();
  };

  if (loading || !sop) return <div className="text-slate-400">Loading…</div>;

  const doneCount = sop.steps.filter((s) => s.done).length;
  const totalCount = sop.steps.length;
  const pct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div>
      <Link to="/sops" className="inline-flex items-center text-amber-300 text-sm hover:text-amber-200 mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to SOPs
      </Link>
      <PageHeader
        icon={BookOpen}
        title={sop.title}
        subtitle={sop.purpose}
        actions={isManager && (
          <Button onClick={openEdit} variant="outline" className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10" data-testid="edit-sop-btn">
            <Edit3 className="h-4 w-4 mr-1" /> Edit SOP
          </Button>
        )}
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Badge variant="outline" className="border-amber-500/40 text-amber-200">Responsible: {sop.responsible}</Badge>
        <Badge variant="outline" className="border-amber-500/40 text-amber-200">v{(sop.versions || []).length || 1}</Badge>
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">{pct}% complete</Badge>
      </div>

      <ComplianceBanner />

      <Tabs defaultValue="checklist" className="mt-6">
        <TabsList className="bg-[#0f2240] border border-amber-500/15">
          <TabsTrigger value="checklist" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-100">Checklist</TabsTrigger>
          <TabsTrigger value="attachments" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-100">Files ({(sop.attachments || []).length})</TabsTrigger>
          <TabsTrigger value="versions" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-100">Versions ({(sop.versions || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="checklist" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            {sop.steps.length === 0 ? (
              <p className="text-slate-400 text-sm">No checklist steps yet.</p>
            ) : (
              <ul className="space-y-3">
                {sop.steps.map((s, idx) => (
                  <li key={s.id} className="flex items-start gap-3 group">
                    <Checkbox
                      data-testid={`sop-step-${idx}`}
                      checked={!!s.done}
                      onCheckedChange={(v) => toggleStep(s.id, !!v)}
                      className="mt-1 border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    <div className="flex-1">
                      <span className={`text-sm ${s.done ? 'line-through text-slate-500' : 'text-slate-100'}`}>
                        <span className="font-bold text-amber-300 mr-2">{idx + 1}.</span>{s.text}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-white">Supporting files</h4>
              {user?.role !== 'Client' && (
                <>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="sop-file-input" />
                  <Button data-testid="sop-upload-btn" onClick={() => fileInputRef.current?.click()} variant="outline" className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                    <Upload className="h-4 w-4 mr-1" /> Upload
                  </Button>
                </>
              )}
            </div>
            {(sop.attachments || []).length === 0 ? (
              <p className="text-slate-400 text-sm">No files yet.</p>
            ) : (
              <ul className="divide-y divide-amber-500/5">
                {sop.attachments.map((a) => (
                  <li key={a.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-5 w-5 text-amber-300 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-white truncate">{a.filename}</div>
                        <div className="text-xs text-slate-500">{a.mime_type} · by {a.uploaded_by} · {new Date(a.uploaded_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button onClick={() => downloadAttachment(a)} variant="ghost" size="icon" className="text-amber-300 hover:bg-amber-500/10"><Download className="h-4 w-4" /></Button>
                      {isManager && (
                        <Button onClick={() => deleteAttachment(a)} variant="ghost" size="icon" className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="versions" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-amber-300" />
              <h4 className="font-bold text-white">Version history</h4>
            </div>
            <ul className="space-y-3">
              {[...(sop.versions || [])].reverse().map((v) => (
                <li key={v.version} className="border border-amber-500/10 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200">v{v.version}</Badge>
                    <span className="text-xs text-slate-500">{new Date(v.edited_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-white mt-2 font-medium">{v.title}</div>
                  <div className="text-xs text-slate-400 mt-1">By {v.edited_by} — {v.note}</div>
                </li>
              ))}
            </ul>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">Edit SOP</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            <div><Label>Purpose</Label><Textarea value={editForm.purpose} onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            <div><Label>Responsible</Label><Input value={editForm.responsible} onChange={(e) => setEditForm({ ...editForm, responsible: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            <div><Label>Steps (one per line)</Label><Textarea rows={8} value={editForm.steps} onChange={(e) => setEditForm({ ...editForm, steps: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            <div><Label>Change note</Label><Input value={editForm.change_note} onChange={(e) => setEditForm({ ...editForm, change_note: e.target.value })} placeholder="What's changing in this version?" className="bg-[#0f2240] border-amber-500/20" /></div>
          </div>
          <DialogFooter><Button onClick={saveEdit} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]">Save new version</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
